import { useMemo } from "react";
import { createFileRoute, useNavigate, type SearchSchemaInput } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ReportPage, type SortOverride } from "@/components/reporting/report-page";
import { bulkEditEntrySearch } from "@/components/bulk-edits/entry/entry-search";
import { useAppStore } from "@/store";

/**
 * The run-time sort override, as the URL carries it: one reference and one
 * direction, which is what a clicked header is. It is a PAIR — a reference with
 * no direction orders nothing and a direction with no reference orders nothing
 * — so the two stand or fall together.
 */
interface ReportSearch {
  sort?: string;
  dir?: "asc" | "desc";
}

/**
 * `SearchSchemaInput` keeps the INPUT half optional while the output stays the
 * validated pair: without it TanStack infers the input from the output and
 * every `to: "/reports/$id"` — the list row, both builders, the dashboard's own
 * exits — would have to spell an order it has no opinion about.
 */
type ReportSearchInput = { sort?: string; dir?: string } & SearchSchemaInput;

/**
 * An allow-list read, the `parseTab` idiom one entity up: a half-written or
 * misspelt order is DROPPED whole rather than repaired, because repairing it
 * would show the reader a sequence they did not ask for and call it theirs.
 */
function parseSortSearch(search: ReportSearchInput): ReportSearch {
  const sort: unknown = search.sort;
  const dir: unknown = search.dir;
  if (typeof sort !== "string" || sort === "") return {};
  if (dir !== "asc" && dir !== "desc") return {};
  return { sort, dir };
}

export const Route = createFileRoute("/_authenticated/reports/$id/")({
  component: ReportDetailRoute,
  validateSearch: parseSortSearch,
});

/**
 * One saved report, run. The page owns the run, both renderings, the drill, the
 * export and the delete confirm; the route owns the page gutter, the sort
 * override the URL carries, and where each of its exits leads — a bulk edit on
 * the report's rows opens the new-edit flow's Change step on the saved report.
 *
 * The override rides the URL so a reader can hand over the report AS THEY ARE
 * READING IT, and every write replaces, so history keeps one entry per visit
 * rather than one per header click — the `?tab=` precedent one entity up.
 *
 * "Open as report" leaves with the document the SERVER derived from the drill,
 * as a row document: the derived query is a set of rows and there is no drawing
 * to carry over.
 */
function ReportDetailRoute() {
  const { id } = Route.useParams();
  const { sort, dir } = Route.useSearch();
  const { t } = useTranslation("org");
  const navigate = useNavigate();
  const orgId = useAppStore((s) => s.activeOrgId);

  // Held on the two primitives: the page keys its run, its grid order and its
  // paging on this object's identity, so a fresh one per render would re-derive
  // all three on every keystroke elsewhere on the page.
  const sortOverride = useMemo<SortOverride | undefined>(
    () => (sort === undefined || dir === undefined ? undefined : { ref: sort, dir }),
    [sort, dir],
  );

  const backToList = () => {
    void navigate({ to: "/reports" });
  };

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("noOrgContext")}</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <ReportPage
        orgId={orgId}
        reportId={id}
        sortOverride={sortOverride}
        onSortOverrideChange={(next) => {
          void navigate({
            to: "/reports/$id",
            params: { id },
            search: next === undefined ? {} : { sort: next.ref, dir: next.dir },
            replace: true,
          });
        }}
        onEdit={(reportId) => {
          void navigate({ to: "/reports/$id/edit", params: { id: reportId } });
        }}
        onOpenAsReport={(derived) => {
          void navigate({
            to: "/reports/new",
            state: {
              reportSeed: { query: derived, visualization: { schema_version: 1, kind: "table" } },
            },
          });
        }}
        onDeleted={backToList}
        onBack={backToList}
        onBulkEdit={(recordType, reportId) => {
          void navigate({
            to: "/bulk-edits/new",
            search: bulkEditEntrySearch(recordType, {
              source: "saved_report",
              report_id: reportId,
            }),
          });
        }}
      />
    </div>
  );
}
